/**
 * Patient Profile overhaul — "Needs attention": the server's tiles first, this file's five as the fallback.
 *
 * Plan: docs/plans/2026-10-08-patient-profile-overhaul-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── SERVER FIRST, LOCAL FALLBACK (2026-11-03) ───────────────────────────────────────────────────
 * On Schedule View the panel's "Needs attention" tiles come from POST /schedule-view/alerts. The
 * Patient Profile page asks the SAME endpoint in its patient mode (`{ siteID, date, patNum }`) and
 * draws the panel's own tiles for the same patient — ten server rules plus the browser's live `task`
 * count — so the two surfaces cannot drift (`resolveProfileAlerts` below picks the source).
 * Only when the server cannot answer (the office is refused — switch off, Teamio app below 3.0.61 —
 * the call fails, or the stage has no schedule-view-service stack yet, which is every prod office
 * today) does the page fall back to the five tiles built HERE from what it has already loaded, with
 * the note PROFILE_LOCAL_NOTE. The fallback payload has the same shape as the server's —
 * `{ date, byPatNum: { [PatNum]: { key: detail } }, ran }` — and the panel's own code
 * (visibleAlertKeys + describeAlert in src/utils/scheduleView/alerts.js) draws it unchanged.
 * Every detail object below is the shape the server's rule produces, so the sentences match.
 *
 * ── `ran` IS THE HONESTY ─────────────────────────────────────────────────────────────────────────
 * A key is listed in `ran` only when this file could actually decide it. A key it could not decide
 * (the source has not answered yet, or the data cannot say) is left OUT — never reported as "clean".
 * Recall, unscheduled-at-a-cleaning, claims, forms, medical history and signatures need data the
 * PATIENT_PROFILE query does not carry, so they are never in `ran` here (PROFILE_ALERT_KEYS).
 *
 * ── THE RULES, EACH MIRRORING schedule-view-service/alerts/<key>.js ──────────────────────────────
 *   premed     patient.Premed === 1. "Requires" — nothing records that it was taken.
 *   balance    The FAMILY's balance (OD bills the guarantor). Decidable only when this patient IS the
 *              guarantor: the profile row's PatientAmount is then the family figure. Fires at
 *              >= $0.01 (the server's floor with the default minimum of 0); credits never fire.
 *   followup   The upcomingFollowups rows, minus stopped ones (Nilay 2026-09-20: a stopped follow-up
 *              is history, not a prompt). Types named from the server's TASK_TYPES, sorted.
 *              pastDue = a FollowupDate before today; a NULL date is normal, never "overdue".
 *   insverify  The Insurance Verification status the page already fetches: never verified, or stale
 *              by that service's own verdict (`isStale`). No visible plan ⇒ no alert (self-pay).
 *   task       Open tasks tagged with the patient. A COUNT only — task titles carry names and
 *              Firestore bypasses the PHI demo mask.
 */

/** The keys this file can ever decide LOCALLY (the fallback), in the panel's display order (scheduleView ALERT_KEYS). */
export const PROFILE_ALERT_KEYS = ['balance', 'followup', 'premed', 'insverify', 'task'];

/** The line under Needs attention while the tiles are the page's own five (the server could not answer). */
export const PROFILE_LOCAL_NOTE = 'Recall, claims, forms and medical-history checks show on Schedule View.';

/** schedule-view-service/alerts/followup.js TASK_TYPES — the names the server puts in `types`. */
export const FOLLOWUP_TASK_TYPES = {
  2: 'Outstanding Claim',
  3: 'Overdue Recall',
  4: 'Unscheduled Treatment',
  5: 'Outstanding Pre-Auth',
  6: 'Processed Pre-Auth',
  7: 'Patient Balance',
  8: 'Broken Appointment',
};

/** Anything under a cent is zero (OD stores denormalized floats). */
export const BALANCE_FLOOR = 0.01;

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** 'YYYY-MM-DD…' → whole days since 1970 (UTC), or null. Digit maths: no local-time parsing of OD text. */
function dayNumber(value) {
  if (value === null || value === undefined) return null;
  const m = YMD_RE.exec(String(value));
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(t) ? Math.round(t / 86400000) : null;
}

function isStopped(row) {
  return row && (row.FollowupStopped === 1 || row.FollowupStopped === '1' || row.FollowupStopped === true);
}

/** The balance detail, or undefined (no alert), or null (undecidable — keep 'balance' out of `ran`). */
export function balanceAlert(patient) {
  if (!patient) return null;
  // Not the guarantor: the family figure lives on someone else's row, which this page did not load.
  if (patient.Guarantor === null || patient.Guarantor === undefined) return null;
  if (Number(patient.Guarantor) !== Number(patient.PatNum)) return null;
  const amount = Number(patient.PatientAmount);
  if (!Number.isFinite(amount) || amount < BALANCE_FLOOR) return undefined;
  return {
    amount,
    guarantor: { PatNum: patient.PatNum, LName: patient.LName, FName: patient.FName },
  };
}

/** The follow-up detail, or undefined (no open follow-ups). */
export function followupAlert(rows, today) {
  const open = (Array.isArray(rows) ? rows : []).filter(r => r && !isStopped(r));
  if (!open.length) return undefined;
  const types = [];
  const todayN = dayNumber(today);
  let pastDue = false;
  open.forEach((r) => {
    const name = FOLLOWUP_TASK_TYPES[Number(r.TaskTypeID)];
    if (name && types.indexOf(name) === -1) types.push(name);
    const due = dayNumber(r.FollowupDate);
    if (due !== null && todayN !== null && due < todayN) pastDue = true;
  });
  types.sort();
  return { count: open.length, types, pastDue };
}

/** The insurance detail, or undefined (verified and fresh, or nothing to verify). */
export function insverifyAlert(insurance, today) {
  if (!insurance) return undefined;
  const plans = Array.isArray(insurance.plans) ? insurance.plans : null;
  if (plans && plans.length === 0) return undefined;
  if (insurance.neverVerified) return { neverVerified: true };
  if (!insurance.isStale) return undefined;
  const last = dayNumber(insurance.lastVerified);
  const now = dayNumber(today);
  const detail = { neverVerified: false };
  if (last !== null && now !== null) detail.ageDays = Math.max(0, now - last);
  return detail;
}

/** Open tasks (not completed, not deleted) tagged with the patient. */
export function openTaskCount(tasks) {
  return (Array.isArray(tasks) ? tasks : []).filter((t) => {
    if (!t || t.deleted === true) return false;
    return !(t.status && String(t.status).toLowerCase() === 'completed');
  }).length;
}

/**
 * Build the alerts payload for ONE patient.
 *
 * Pass `undefined` for a source that has not answered yet; its key stays out of `ran`.
 *
 * @param {object} p
 * @param {object|null} p.patient     the adapted patient row (profileAdapter), or null
 * @param {Array} [p.followups]       upcomingFollowups rows; undefined = not loaded
 * @param {object|null} [p.insurance] Insurance Verification status; undefined = not loaded,
 *                                    null = feature off / failed (never in `ran`)
 * @param {Array} [p.tasks]           tasks tagged with the patient; undefined = not loaded
 * @param {string} p.today            YYYY-MM-DD in the site's timezone
 */
export function buildProfileAlertPayload({ patient, followups, insurance, tasks, today } = {}) {
  const payload = { date: today || null, byPatNum: {}, ran: [] };
  if (!patient || patient.PatNum === null || patient.PatNum === undefined) return payload;

  const found = {};
  const ran = [];

  const balance = balanceAlert(patient);
  if (balance !== null) {
    ran.push('balance');
    if (balance) found.balance = balance;
  }

  if (Array.isArray(followups)) {
    ran.push('followup');
    const f = followupAlert(followups, today);
    if (f) found.followup = f;
  }

  ran.push('premed');
  if (Number(patient.Premed) === 1) found.premed = {};

  if (insurance) {
    ran.push('insverify');
    const i = insverifyAlert(insurance, today);
    if (i) found.insverify = i;
  }

  if (Array.isArray(tasks)) {
    ran.push('task');
    const n = openTaskCount(tasks);
    if (n > 0) found.task = { count: n };
  }

  payload.ran = PROFILE_ALERT_KEYS.filter(k => ran.indexOf(k) !== -1);
  if (Object.keys(found).length) payload.byPatNum[String(patient.PatNum)] = found;
  return payload;
}

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * The server's patient-mode answer, but only if it is one the page may draw; else null.
 *
 * `server` is what the page stored: `{ patNum, data }` — the PatNum the question was ASKED for and the
 * response's `data`. Null (→ the local fallback) when:
 *   • nothing has answered, or the answer was asked for ANOTHER patient (a late reply after the page
 *     moved on must never draw one patient's tiles under another);
 *   • the service answered `unavailable` (e.g. NO_OD_DATA — a site with no Open Dental mirror yet);
 *   • the body is not the documented shape (`byPatNum` object + `ran` array) — a half-answer is no answer.
 */
export function usableServerAlerts(server, patNum) {
  if (!isPlainObject(server) || !isPlainObject(server.data)) return null;
  if (String(server.patNum) !== String(patNum)) return null;
  const data = server.data;
  if (data.unavailable) return null;
  if (!isPlainObject(data.byPatNum) || !Array.isArray(data.ran)) return null;
  return data;
}

/**
 * The live task counts in the form the panel folds in (alerts.js mergeTaskAlerts): null while the
 * listener has not answered (→ no `task` tile, never a confident 0), `{}` for none, else `{ "<PatNum>": n }`.
 */
export function taskCountsFor(patNum, tasks) {
  if (!Array.isArray(tasks)) return null;
  const n = openTaskCount(tasks);
  const counts = {};
  if (n > 0) counts[String(patNum)] = n;
  return counts;
}

/**
 * The line under Needs attention when some of the server's rules FAILED ('' when none did).
 *
 * ⛔ Not decoration: a rule that failed and a rule that found nothing both draw no tile, and "no
 * balance tile" reads as "nothing owed". Schedule View says so on its page (alerts.js
 * failedAlertsSentence — worded for the day's Alerts list, which this page does not have).
 *
 * @param {Array} keys      the response's `errors`
 * @param {Function} labelOf  key -> the alert's words (settingsForm.js ALERT_META); injected
 */
export function failedChecksNote(keys, labelOf) {
  const list = Array.isArray(keys) ? keys.filter(k => typeof k === 'string' && k) : [];
  if (!list.length) return '';
  const label = typeof labelOf === 'function' ? labelOf : () => '';
  return `Could not be checked, so not shown here: ${list.map(k => label(k) || k).join(', ')}.`;
}

/**
 * What the Patient Profile page's Needs attention draws: the payload to read, the keys to draw (in
 * order) and the line under them.
 *
 * SERVER (the answer is usable — usableServerAlerts): the panel's own pipeline, step for step —
 * `mergeTaskAlerts(data, live task counts)` then `blockAlertKeys(payload, patNum, viewSettings, ALERT_KEYS)`
 * — so the keys, their order, the office's own alert switches (`task` is the one that matters
 * client-side) and, via describeAlert, the sentences are Schedule View's. The note is '' unless a
 * rule failed (failedChecksNote).
 *
 * LOCAL (anything else): today's behaviour exactly — the page's own five (buildProfileAlertPayload,
 * PROFILE_ALERT_KEYS, no office switches) and PROFILE_LOCAL_NOTE.
 *
 * `deps` carries the three helpers from src/utils/scheduleView/alerts.js (`mergeTaskAlerts`,
 * `blockAlertKeys`, `visibleAlertKeys`), injected because this file stays import-free.
 *
 * @param {object} p
 * @param {?{patNum: (string|number), data: object}} p.server  the stored server answer, or null
 * @param {object} p.local          buildProfileAlertPayload(...) for this patient
 * @param {Array}  [p.tasks]        the patient's open-task rows; undefined = the listener has not answered
 * @param {(string|number)} p.patNum  the patient on screen
 * @param {?{alerts: object}} p.viewSettings  the office's switches (practiceOps/viewSettings.js officeViewSettings)
 * @param {Array}  p.order          the panel's ALERT_KEYS
 * @param {Function} [p.labelOf]    key -> label, for the failed-rule note
 * @param {{mergeTaskAlerts: Function, blockAlertKeys: Function, visibleAlertKeys: Function}} p.deps
 * @returns {{source: ('server'|'local'), payload: object, keys: string[], note: string}}
 */
export function resolveProfileAlerts({ server, local, tasks, patNum, viewSettings, order, labelOf, deps } = {}) {
  const data = usableServerAlerts(server, patNum);
  if (!data) {
    return {
      source: 'local',
      payload: local,
      keys: deps.visibleAlertKeys(local, patNum, PROFILE_ALERT_KEYS),
      note: PROFILE_LOCAL_NOTE,
    };
  }
  const payload = deps.mergeTaskAlerts(data, taskCountsFor(patNum, tasks));
  return {
    source: 'server',
    payload,
    keys: deps.blockAlertKeys(payload, patNum, viewSettings, order),
    note: failedChecksNote(data.errors, labelOf),
  };
}
